import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { CHECKLIST_POINTS, sitesForSub, allSites } from "./checklistData";
import AuthedImage from "@/components/AuthedImage";
import AuthedPdf from "@/components/AuthedPdf";
import { isImage } from "@/lib/files";
import { NavLogPreview, FuelPreview, WBPreview, LimitationsPreview } from "./previews";
import { fmt, minToHm } from "@/lib/units";
import { X, ChevronLeft, ChevronRight, CheckCircle2, Circle, FileText, Map, AlertTriangle, Eye, EyeOff, PlaneTakeoff, ExternalLink } from "lucide-react";

const DOC_LABELS = {
  licence: "Licence (PPL/LAPL)", medical: "Certificat médical",
  manuel_vol: "Manuel de vol", fiche_pesee: "Fiche de pesée",
  immatriculation: "Certificat d'immatriculation", acoustique: "Certificat acoustique",
  cdn: "CDN / CEN", licence_station: "Licence station d'aéronef", assurance: "Assurance",
};
const isExpired = (d) => d && new Date(d) < new Date(new Date().toDateString());

export default function PresentationMode({ briefing, nav, aircraft, briefingId, onClose }) {
  const [files, setFiles] = useState([]);
  const [pilotDocs, setPilotDocs] = useState([]);
  const [acDocs, setAcDocs] = useState([]);
  const [i, setI] = useState(0);
  const [zoom, setZoom] = useState(null);

  useEffect(() => { api.get(`/briefings/${briefingId}/files`).then((r) => setFiles(r.data)).catch(() => {}); }, [briefingId]);
  useEffect(() => { if (briefing.pilotId) api.get(`/pilots/${briefing.pilotId}/documents`).then((r) => setPilotDocs(r.data)).catch(() => {}); }, [briefing.pilotId]);
  useEffect(() => { if (briefing.aircraftId) api.get(`/aircraft/${briefing.aircraftId}/documents`).then((r) => setAcDocs(r.data)).catch(() => {}); }, [briefing.aircraftId]);

  useEffect(() => {
    const el = document.documentElement;
    el.requestFullscreen?.().catch(() => {});
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    };
  }, []);

  const ctx = { briefing, nav, aircraft, briefingId, files, pilotDocs, acDocs, onZoom: setZoom };
  const slides = buildSlides(ctx);

  const next = useCallback(() => setI((x) => Math.min(x + 1, slides.length - 1)), [slides.length]);
  const prev = useCallback(() => setI((x) => Math.max(x - 1, 0)), []);

  useEffect(() => {
    const onKey = (e) => {
      if (zoom) { if (e.key === "Escape") setZoom(null); return; }
      if (e.key === "ArrowRight" || e.key === " ") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, onClose, zoom]);

  const slide = slides[Math.min(i, slides.length - 1)];

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex flex-col bg-[#070C18]" data-testid="presentation-mode">
      <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3 sm:px-6 sm:py-4">
        <button onClick={onClose} data-testid="presentation-close" title="Revenir à la check-list"
          className="flex items-center gap-2 rounded-lg px-2 py-2 text-slate-200 hover:bg-slate-800 hover:text-rose-300">
          <X className="h-6 w-6" /> <span className="hidden text-sm font-medium sm:inline">Check-list</span>
        </button>
        <div className="truncate px-2 font-mono-data text-sm text-cyan-500/80">{briefing.name}</div>
        <div className="shrink-0 font-mono-data text-sm text-slate-400">{i + 1} / {slides.length}</div>
      </div>

      <div className="relative flex flex-1 items-start justify-center overflow-y-auto px-8 py-6">
        {slide.purpose && (
          <div className="pointer-events-none absolute right-6 top-4 max-w-[240px] text-right text-[11px] italic leading-snug text-slate-500">
            {slide.purpose}
          </div>
        )}
        <div className="animate-fade-up w-full max-w-5xl" key={i}>
          <div className="font-mono-data text-sm uppercase tracking-widest text-cyan-500/70">{slide.eyebrow}</div>
          <h1 className="mt-1 font-display text-3xl font-bold text-slate-50 lg:text-5xl">{slide.title}</h1>
          <div className="mt-6 pb-8">{slide.body}</div>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-slate-800 px-8 py-4">
        <button onClick={prev} disabled={i === 0} data-testid="presentation-prev" className="flex items-center gap-2 rounded-lg px-4 py-2 text-slate-300 hover:bg-slate-800 disabled:opacity-30"><ChevronLeft className="h-5 w-5" /> Précédent</button>
        <div className="flex flex-wrap gap-1.5">
          {slides.map((_, x) => <div key={x} className={`h-1.5 rounded-full transition-all ${x === i ? "w-8 bg-cyan-400" : "w-1.5 bg-slate-700"}`} />)}
        </div>
        <button onClick={next} disabled={i === slides.length - 1} data-testid="presentation-next" className="flex items-center gap-2 rounded-lg px-4 py-2 text-slate-300 hover:bg-slate-800 disabled:opacity-30">Suivant <ChevronRight className="h-5 w-5" /></button>
      </div>

      {zoom && (
        <div onClick={() => setZoom(null)} data-testid="lightbox"
          className="fixed inset-0 z-[110] flex flex-col items-center justify-center bg-black/90 p-6">
          <button onClick={() => setZoom(null)} className="absolute right-6 top-6 rounded-lg bg-white/10 p-2 text-white hover:bg-white/20"><X className="h-6 w-6" /></button>
          <AuthedImage path={zoom} alt="agrandi" className="max-h-[85vh] max-w-[90vw] rounded-lg object-contain" />
          <p className="mt-3 text-xs text-slate-400">Cliquez ou Échap pour revenir à la diapo</p>
        </div>
      )}
    </div>,
    document.body
  );
}

function Gallery({ files, briefingId, onZoom, big }) {
  const imgs = files.filter((f) => isImage(f.content_type));
  const docs = files.filter((f) => !isImage(f.content_type));
  if (!imgs.length && !docs.length) return null;
  return (
    <div className="mt-3">
      {imgs.length > 0 && (
        big ? (
          <div className="space-y-4">
            {imgs.map((f) => {
              const path = `/briefings/${briefingId}/files/${f.id}/download`;
              return <AuthedImage key={f.id} path={path} alt={f.original_filename} onClick={() => onZoom(path)}
                className="max-h-[78vh] w-full cursor-zoom-in rounded-xl border border-slate-800 object-contain bg-slate-950/40" />;
            })}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {imgs.map((f) => {
              const path = `/briefings/${briefingId}/files/${f.id}/download`;
              return <AuthedImage key={f.id} path={path} alt={f.original_filename} onClick={() => onZoom(path)}
                className="max-h-48 w-full cursor-zoom-in rounded-lg border border-slate-800 object-contain bg-slate-950/40" />;
            })}
          </div>
        )
      )}
      {docs.map((f) => {
        const path = `/briefings/${briefingId}/files/${f.id}/download`;
        return (
          <div key={f.id} className="mt-2">
            <div className="mb-1 flex items-center gap-1.5 text-xs text-slate-400"><FileText className="h-3.5 w-3.5" /> {f.original_filename}</div>
            <AuthedPdf path={path} className={`w-full rounded-lg border border-slate-800 bg-slate-950/40 ${big ? "h-[75vh]" : "h-80"}`} />
          </div>
        );
      })}
    </div>
  );
}

function DocGallery({ docs, entityType, entityId, onZoom }) {
  if (!entityId) return <p className="text-slate-500">Aucune fiche associée.</p>;
  if (!docs.length) return <p className="text-slate-500">Aucun document dans la fiche.</p>;
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
      {docs.map((d) => {
        const path = `/${entityType}/${entityId}/documents/${d.id}/download`;
        const expired = isExpired(d.validity_date);
        return (
          <div key={d.id} className="rounded-lg border border-slate-800 bg-slate-950/40 p-2">
            {isImage(d.content_type)
              ? <AuthedImage path={path} alt={d.doc_type} onClick={() => onZoom(path)} className="h-40 w-full cursor-zoom-in rounded object-contain" />
              : <AuthedPdf path={path} className="h-56 w-full rounded bg-slate-950/40" />}
            <div className="mt-2 text-center text-xs font-medium text-slate-200">{DOC_LABELS[d.doc_type] || d.label || d.doc_type}</div>
            {d.validity_date && (
              <div className={`mt-0.5 flex items-center justify-center gap-1 text-center text-[11px] ${expired ? "text-rose-300" : "text-emerald-300"}`}>
                {expired && <AlertTriangle className="h-3 w-3" />} Validité : {d.validity_date}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Subs({ point, state, files, briefingId, onZoom, briefing }) {
  const customSubs = state.customSubs || [];
  const subs = [
    ...(point.fields || []).map((f) => ({ key: f.key, label: f.label })),
    ...customSubs.map((c) => ({ key: c.key, label: c.label })),
  ];
  const filesFor = (pid) => files.filter((f) => String(f.point_id) === String(pid));
  const general = filesFor(`${point.id}`);
  const sites = allSites(briefing);
  return (
    <div className="space-y-4">
      {subs.map((sub) => {
        const text = state[sub.key];
        const sf = filesFor(`${point.id}::${sub.key}`);
        const ids = state.subSites?.[sub.key] ?? sitesForSub(briefing, `${point.id}::${sub.key}`).map((s) => s.id);
        const links = sites.filter((s) => ids.includes(s.id));
        if (!text && sf.length === 0 && links.length === 0) return null;
        return (
          <div key={sub.key} className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
            <div className="text-xs uppercase tracking-wider text-cyan-500/70">{sub.label}</div>
            {text && <div className="mt-1 whitespace-pre-wrap text-slate-200">{text}</div>}
            <Gallery files={sf} briefingId={briefingId} onZoom={onZoom} />
            {links.length > 0 && (
              <div className="mt-2 flex flex-wrap justify-end gap-x-3 gap-y-1">
                {links.map((s) => (
                  <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] text-cyan-400 hover:underline">
                    {s.url.replace(/^https?:\/\//, "").replace(/\/$/, "")} <ExternalLink className="h-3 w-3" />
                  </a>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {general.length > 0 && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
          <div className="text-xs uppercase tracking-wider text-cyan-500/70">Documents</div>
          <Gallery files={general} briefingId={briefingId} onZoom={onZoom} />
        </div>
      )}
    </div>
  );
}

function NavPointBody({ nav, briefing, navmapFiles, briefingId, onZoom, point, state, files }) {
  const [showMap, setShowMap] = useState(false);
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 rounded-lg border border-cyan-500/25 bg-slate-900/40 px-4 py-3" data-testid="slide-departure">
        <PlaneTakeoff className="h-5 w-5 text-cyan-400" />
        <span className="text-sm uppercase tracking-wider text-slate-400">Aérodrome de départ</span>
        <span className="ml-auto font-mono-data text-xl font-bold text-cyan-300">{briefing.departure || "—"}</span>
      </div>
      <NavLogPreview nav={nav} big />
      <div>
        <button onClick={() => setShowMap((s) => !s)} data-testid="toggle-navmap-slide"
          className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900/40 px-4 py-2.5 text-sm font-medium text-cyan-300 hover:bg-slate-800">
          <Map className="h-4 w-4" /> Carte de navigation
          {showMap ? <EyeOff className="ml-1 h-4 w-4" /> : <Eye className="ml-1 h-4 w-4" />}
        </button>
        {showMap && (
          <div className="mt-3 rounded-xl border border-slate-800 bg-slate-900/40 p-4">
            {briefing.navMapComment && <p className="mb-2 whitespace-pre-wrap text-slate-200">{briefing.navMapComment}</p>}
            <Gallery files={navmapFiles} briefingId={briefingId} onZoom={onZoom} big />
          </div>
        )}
      </div>
      <Subs point={point} state={state} files={files} briefingId={briefingId} onZoom={onZoom} briefing={briefing} />
    </div>
  );
}

function buildSlides({ briefing, nav, aircraft, briefingId, files, pilotDocs, acDocs, onZoom }) {
  const checklist = briefing.checklist || {};
  const navmapFiles = files.filter((f) => String(f.point_id) === "navmap");

  const intro = {
    eyebrow: "Briefing VFR",
    title: briefing.name,
    body: (
      <div>
        <div className="mb-4 space-y-1 font-mono-data text-xl text-cyan-200">
          <div>{briefing.departure || "?"} → {briefing.arrival || "?"}</div>
          <div className="text-base text-slate-400">{fmt(nav.totalDistance)} NM · {minToHm(nav.totalTime)} · {fmt(nav.totalFuel, 1)} L</div>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
          <div className="mb-3 flex items-center gap-1.5 text-xs uppercase tracking-wider text-cyan-500/70"><Map className="h-3.5 w-3.5" /> Carte de navigation</div>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(200px,1fr)_2.2fr]">
            <div className="whitespace-pre-wrap text-base leading-relaxed text-slate-200">
              {briefing.navMapComment || <span className="italic text-slate-500">Aucun commentaire</span>}
            </div>
            <Gallery files={navmapFiles} briefingId={briefingId} onZoom={onZoom} big />
          </div>
        </div>
      </div>
    ),
  };

  const points = CHECKLIST_POINTS.map((p) => {
    const state = checklist[p.id] || {};
    let body;
    if (p.id === 1) body = <DocGallery docs={pilotDocs} entityType="pilots" entityId={briefing.pilotId} onZoom={onZoom} />;
    else if (p.id === 2) body = <DocGallery docs={acDocs} entityType="aircraft" entityId={briefing.aircraftId} onZoom={onZoom} />;
    else if (p.id === 5) body = <NavPointBody nav={nav} briefing={briefing} navmapFiles={navmapFiles} briefingId={briefingId} onZoom={onZoom} point={p} state={state} files={files} />;
    else if (p.id === 6) body = <div className="space-y-4"><FuelPreview briefing={briefing} nav={nav} aircraft={aircraft} /><Subs point={p} state={state} files={files} briefingId={briefingId} onZoom={onZoom} briefing={briefing} /></div>;
    else if (p.id === 7) body = <div className="space-y-4"><WBPreview briefing={briefing} aircraft={aircraft} nav={nav} /><Subs point={p} state={state} files={files} briefingId={briefingId} onZoom={onZoom} briefing={briefing} /></div>;
    else if (p.id === 8) body = <div className="space-y-4"><LimitationsPreview briefing={briefing} /><Subs point={p} state={state} files={files} briefingId={briefingId} onZoom={onZoom} briefing={briefing} /></div>;
    else body = <Subs point={p} state={state} files={files} briefingId={briefingId} onZoom={onZoom} briefing={briefing} />;

    return {
      eyebrow: `Point ${p.id} / 11`,
      title: p.title,
      purpose: p.id === 5 ? undefined : p.purpose,
      body: (
        <div>
          <div className="mb-4 flex items-center gap-2 text-sm">
            {state.done ? <CheckCircle2 className="h-5 w-5 text-emerald-400" /> : <Circle className="h-5 w-5 text-slate-600" />}
            <span className={state.done ? "text-emerald-300" : "text-slate-500"}>{state.done ? "Vérifié" : "À vérifier"}</span>
          </div>
          {body}
        </div>
      ),
    };
  });

  return [intro, ...points];
}
